import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash, FaCheck, FaTrophy } from 'react-icons/fa';
import PortfolioService from '../services/PortfolioService';
import toast from 'react-hot-toast';
import './PortfolioSelector.css';

const PortfolioSelector = ({ activePortfolio, onPortfolioChange }) => {
  const [portfolios, setPortfolios] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState(null);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [newPortfolioLeague, setNewPortfolioLeague] = useState('10k');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadPortfolios();
  }, []);

  useEffect(() => {
    if (activePortfolio) {
      loadPortfolios();
    }
  }, [activePortfolio]);

  const loadPortfolios = async () => {
    try {
      const data = await PortfolioService.getPortfolios();
      // Separate regular and challenge portfolios
      const allPortfolios = data.portfolios || [];
      setPortfolios(allPortfolios);
    } catch (error) {
      console.error('Failed to load portfolios:', error);
      // If portfolios route doesn't exist, try to get single portfolio
      try {
        const portfolioData = await PortfolioService.getPortfolio();
        if (portfolioData.portfolio) {
          setPortfolios([portfolioData.portfolio]);
        }
      } catch (fallbackError) {
        // Silently fail - portfolios feature may not be available
      }
    }
  };

  const handleActivatePortfolio = async (portfolioId) => {
    try {
      setLoading(true);
      const response = await PortfolioService.activatePortfolio(portfolioId);
      // Use the portfolio returned from the activation call
      const activatedPortfolio = response.portfolio;
      await loadPortfolios();
      // Update parent component with the activated portfolio
      if (activatedPortfolio) {
        onPortfolioChange(activatedPortfolio);
      } else {
        // Fallback: fetch active portfolio if not in response
        const activeData = await PortfolioService.getActivePortfolio();
        if (activeData.portfolio) {
          onPortfolioChange(activeData.portfolio);
        }
      }
      setIsOpen(false);
      toast.success('Portfolio activated');
    } catch (error) {
      toast.error(error.message || 'Failed to activate portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    if (!newPortfolioName.trim()) {
      toast.error('Portfolio name is required');
      return;
    }

    // Count only regular portfolios (not challenge portfolios)
    const regularPortfolios = portfolios.filter(p => !p.challengeId);
    if (regularPortfolios.length >= 3) {
      toast.error('Maximum of 3 portfolios allowed');
      return;
    }

    try {
      setLoading(true);
      await PortfolioService.createPortfolio(newPortfolioName.trim(), newPortfolioLeague);
      await loadPortfolios();
      setNewPortfolioName('');
      setNewPortfolioLeague('10k');
      setShowCreateModal(false);
      toast.success('Portfolio created successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to create portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPortfolio = async (e) => {
    e.preventDefault();
    if (!newPortfolioName.trim()) {
      toast.error('Portfolio name is required');
      return;
    }

    try {
      setLoading(true);
      await PortfolioService.updatePortfolioName(editingPortfolio.id, newPortfolioName.trim());
      await loadPortfolios();
      setShowEditModal(false);
      setEditingPortfolio(null);
      setNewPortfolioName('');
      toast.success('Portfolio updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update portfolio');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePortfolio = async (portfolioId) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (portfolio && portfolio.challengeId) {
      toast.error('Challenge portfolios cannot be deleted');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this portfolio? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await PortfolioService.deletePortfolio(portfolioId);
      await loadPortfolios();
      // If deleted portfolio was active, switch to first available
      if (activePortfolio && activePortfolio.id === portfolioId) {
        const data = await PortfolioService.getPortfolios();
        if (data.portfolios && data.portfolios.length > 0) {
          await handleActivatePortfolio(data.portfolios[0].id);
        }
      }
      toast.success('Portfolio deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete portfolio');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (portfolio) => {
    setEditingPortfolio(portfolio);
    setNewPortfolioName(portfolio.name);
    setShowEditModal(true);
  };

  const getLeagueLabel = (league) => {
    const labels = {
      '10k': '$10,000',
      '100k': '$100,000',
      '1M': '$1,000,000'
    };
    return labels[league] || league;
  };

  const formatBalance = (balance) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(balance);
  };

  return (
    <div className="portfolio-selector">
      <button
        className="portfolio-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
      >
        <span className="portfolio-selector-label">
          {activePortfolio ? (
            <>
              <span className="portfolio-name">
                {activePortfolio.name}
                {activePortfolio.challengeId && (
                  <span className="challenge-badge-small">
                    <FaTrophy />
                  </span>
                )}
              </span>
              <span className="portfolio-league">{getLeagueLabel(activePortfolio.league)}</span>
            </>
          ) : (
            'Select Portfolio'
          )}
        </span>
        {isOpen ? <FaChevronUp /> : <FaChevronDown />}
      </button>

      {isOpen && (
        <div className="portfolio-selector-dropdown">
          {/* Regular Portfolios Section */}
          <div className="portfolio-section">
            <div className="portfolio-section-header">
              <h3>My Portfolios</h3>
            </div>
            <div className="portfolio-list">
              {portfolios.filter(p => !p.challengeId).map((portfolio) => {
                const isActive = activePortfolio && activePortfolio.id === portfolio.id;
                return (
                <div
                  key={portfolio.id}
                  className={`portfolio-item ${isActive ? 'active' : ''}`}
                >
                  <div className="portfolio-item-info">
                    <div className="portfolio-item-header">
                      <span className="portfolio-item-name">{portfolio.name}</span>
                      {isActive && <FaCheck className="active-indicator" />}
                    </div>
                    <div className="portfolio-item-details">
                      <span className="portfolio-item-league">{getLeagueLabel(portfolio.league)}</span>
                      <span className="portfolio-item-balance">{formatBalance(portfolio.totalValue || portfolio.balance)}</span>
                    </div>
                  </div>
                  <div className="portfolio-item-actions">
                    {!isActive && (
                      <button
                        className="portfolio-action-btn activate"
                        onClick={() => handleActivatePortfolio(portfolio.id)}
                        disabled={loading}
                      >
                        Activate
                      </button>
                    )}
                    <button
                      className="portfolio-action-btn edit"
                      onClick={() => openEditModal(portfolio)}
                      disabled={loading}
                    >
                      <FaEdit />
                    </button>
                    {!isActive && (
                      <button
                        className="portfolio-action-btn delete"
                        onClick={() => handleDeletePortfolio(portfolio.id)}
                        disabled={loading}
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
            {portfolios.filter(p => !p.challengeId).length < 3 && (
              <button
                className="create-portfolio-btn"
                onClick={() => setShowCreateModal(true)}
                disabled={loading}
              >
                <FaPlus /> Create New Portfolio
              </button>
            )}
          </div>

          {/* Challenge Portfolios Section */}
          {portfolios.filter(p => p.challengeId).length > 0 && (
            <div className="portfolio-section challenge-section">
              <div className="portfolio-section-header">
                <FaTrophy className="section-icon" />
                <h3>Challenge Portfolios</h3>
              </div>
              <div className="portfolio-list">
                {portfolios.filter(p => p.challengeId).map((portfolio) => {
                  const isActive = activePortfolio && activePortfolio.id === portfolio.id;
                  return (
                  <div
                    key={portfolio.id}
                    className={`portfolio-item challenge-portfolio ${isActive ? 'active' : ''}`}
                  >
                    <div className="portfolio-item-info">
                      <div className="portfolio-item-header">
                        <span className="portfolio-item-name">{portfolio.name}</span>
                        {isActive && <FaCheck className="active-indicator" />}
                      </div>
                      <div className="portfolio-item-details">
                        <span className="portfolio-item-league">{getLeagueLabel(portfolio.league)}</span>
                        <span className="portfolio-item-balance">{formatBalance(portfolio.totalValue || portfolio.balance)}</span>
                      </div>
                    </div>
                    <div className="portfolio-item-actions">
                      {!isActive && (
                        <button
                          className="portfolio-action-btn activate"
                          onClick={() => handleActivatePortfolio(portfolio.id)}
                          disabled={loading}
                        >
                          Activate
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Create New Portfolio</h2>
            <form onSubmit={handleCreatePortfolio}>
              <div className="form-group">
                <label>Portfolio Name</label>
                <input
                  type="text"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  placeholder="e.g., Growth Portfolio"
                  maxLength={50}
                  required
                />
              </div>
              <div className="form-group">
                <label>League</label>
                <select
                  value={newPortfolioLeague}
                  onChange={(e) => setNewPortfolioLeague(e.target.value)}
                >
                  <option value="10k">$10,000 League</option>
                  <option value="100k">$100,000 League</option>
                  <option value="1M">$1,000,000 League</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button type="submit" disabled={loading}>Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingPortfolio && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Portfolio</h2>
            <form onSubmit={handleEditPortfolio}>
              <div className="form-group">
                <label>Portfolio Name</label>
                <input
                  type="text"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  placeholder="Portfolio name"
                  maxLength={50}
                  required
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="submit" disabled={loading}>Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioSelector;

